import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { logEvent } from '../utils/analytics';
import './ExamDetailPage.css';
import '../pages/Dashboard.css';

const API_BASE_URL = '/api';

function ExamDetailPage() {
  const navigate = useNavigate();
  const { examId } = useParams();
  const [exam, setExam] = useState(null);
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [activeMenu, setActiveMenu] = useState('exams');

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (userData && examId) {
      loadExamDetails();
    }
  }, [userData, examId]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredBooks(books);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = books.filter(book =>
        book.name.toLowerCase().includes(query) ||
        (book.author && book.author.toLowerCase().includes(query))
      );
      setFilteredBooks(filtered);
    }
  }, [searchQuery, books]);

  const checkSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/check-session`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.authenticated && data.data && data.data.doctor) {
        setUserData(data.data.doctor);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error checking session:', error);
      navigate('/login');
    }
  };

  const loadExamDetails = async () => {
    setIsLoading(true);
    try {
      // Load exam details
      const examResponse = await fetch(`${API_BASE_URL}/exams/${examId}`, {
        credentials: 'include'
      });
      const examData = await examResponse.json();
      if (examData.success) {
        const examInfo = examData.data || examData.exam;
        setExam(examInfo);

        // Track exam detail page view
        logEvent('Exams', 'exam_detail_loaded', {
          exam_id: examId,
          exam_name: examInfo?.name || 'Unknown',
        });
      }

      // Load books for this exam
      const countryStudying = userData?.country_studying;
      const booksUrl = countryStudying 
        ? `${API_BASE_URL}/books?limit=1000&country=${encodeURIComponent(countryStudying)}`
        : `${API_BASE_URL}/books?limit=1000`;
      
      const booksResponse = await fetch(booksUrl, {
        credentials: 'include'
      });
      const booksData = await booksResponse.json();
      if (booksData.success) {
        // Filter books that are associated with this exam
        const allBooks = booksData.data || booksData.books || [];
        const examBooks = allBooks.filter(book => {
          // Check if book is associated with this exam
          if (book.exam_ids && Array.isArray(book.exam_ids)) {
            return book.exam_ids.includes(parseInt(examId));
          }
          return false;
        });

        // Books are already filtered by country in the API call
        setBooks(examBooks);
        setFilteredBooks(examBooks);
      }
    } catch (error) {
      console.error('Error loading exam details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartChat = async (bookId) => {
    const book = books.find(b => b.id === bookId);

    logEvent('Exams', 'exam_book_chat_started', {
      exam_id: examId,
      exam_name: exam?.name || 'Unknown',
      book_id: bookId,
      book_name: book?.name || 'Unknown',
    });

    try {
      // Create a new chat
      const chatResponse = await fetch(`${API_BASE_URL}/student-chats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({})
      });

      const chatData = await chatResponse.json();
      if (chatData.success && chatData.chat) {
        const chatId = chatData.chat.id;

        // Apply book filter to the chat
        await fetch(`${API_BASE_URL}/student-chats/${chatId}/messages/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            message: '',
            book_ids: [bookId]
          })
        });

        // Navigate to chat
        navigate(`/chat/${chatId}`);
      }
    } catch (error) {
      console.error('Error starting chat:', error);
    }
  };

  const handleMenuClick = (menuItem) => {
    setActiveMenu(menuItem);
    if (menuItem === 'home') {
      navigate('/dashboard');
    } else if (menuItem === 'ai-chat') {
      navigate('/chat');
    } else if (menuItem === 'exams') {
      navigate('/exams');
    } else if (menuItem === 'feedback') {
      navigate('/feedback');
    } else if (menuItem === 'profile') {
      navigate('/edit-profile');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/doctor-auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const getMenuItems = () => {
    if (!userData) return [];
    
    if (userData.user_type === 'medical_student') {
      return [
        { id: 'home', label: 'Home', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'ai-chat', label: 'AI Chat', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'exams', label: 'Exams', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 19V5C4 3.89543 4.89543 3 6 3H19.4C19.7314 3 20 3.26863 20 3.6V16.7143" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 17L20 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 21L20 21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 21C4.89543 21 4 20.1046 4 19C4 17.8954 4.89543 17 6 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 7L15 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg> },
        { id: 'feedback', label: 'Feedback', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'profile', label: 'Profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> }
      ];
    } else {
      return [
        { id: 'home', label: 'Home', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'ai-chat', label: 'AI Chat', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'profile', label: 'Profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> }
      ];
    }
  };

  if (isLoading) {
    return (
      <div className="exam-detail-page">
        <div className="exam-detail-loading">Loading exam details...</div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="exam-detail-page">
        <div className="exam-detail-error">Exam not found</div>
      </div>
    );
  }

  const menuItems = getMenuItems().filter(item => item.id !== 'profile');

  return (
    <div className="exam-detail-page">
      {/* Top Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <img src="/logo.svg" alt="S.C.A.L.P.E.L. Logo" className="dashboard-logo-img" />
        </div>
        <nav className="dashboard-nav desktop-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-menu-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.id)}
            >
              <span className="nav-menu-icon">{item.icon}</span>
              <span className="nav-menu-label">{item.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} className="dashboard-logout-btn">
          <svg width="20" height="20" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
            <path d="M12 12H19M19 12L16 15M19 12L16 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
            <path d="M19 6V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V18" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
          </svg>
          <span className="logout-text">Logout</span>
        </button>
      </div>

      <div className="exam-detail-container">
        <button className="exam-detail-back-btn" onClick={() => navigate('/exams')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Exams
        </button>

        <div className="exam-detail-header">
          <h1 className="exam-detail-title">{exam.name}</h1>
          {exam.description && (
            <p className="exam-detail-description">{exam.description}</p>
          )}
        </div>

        <div className="exam-detail-search-wrapper">
          <input
            type="text"
            className="exam-detail-search-input"
            placeholder="Search books by name or author..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <svg className="exam-detail-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        {filteredBooks.length === 0 ? (
          <div className="exam-detail-empty">
            {searchQuery ? 'No books found matching your search.' : 'No books available for this exam.'}
          </div>
        ) : (
          <div className="exam-detail-books-grid">
            {filteredBooks.map((book) => (
              <div key={book.id} className="exam-detail-book-card">
                <div className="exam-detail-book-content">
                  <h3 className="exam-detail-book-title">{book.name}</h3>
                  {book.author && (
                    <p className="exam-detail-book-author">by {book.author}</p>
                  )}
                  {book.status && (
                    <span className={`exam-detail-book-status exam-detail-book-status-${book.status}`}>
                      {book.status}
                    </span>
                  )}
                </div>
                <button
                  className="exam-detail-book-chat-btn"
                  onClick={() => handleStartChat(book.id)}
                >
                  Start Chat
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExamDetailPage;

